/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import { httpRequest } from '../shared/http';
import { getCookie } from '../shared/utils/cookie';
import { sanitize } from '../shared/utils/objectUtils';

const baseUrl = '/epct-integration-api';

const tempUrl = '/npefiling-api/jsonschema/submissions';
const epctROInitUrl = '/ePCTFiling/pages/embedded/init.xhtml';
const epctSFDInitUrl = '/ePCT/embedded/init.xhtml';
const ep1001HfsPaymentMethods = {
  BANK_TRANSFER: 'BANK',
  CREDIT_CARD: 'CCARD',
  DEPOSIT_ACCOUNT: 'DBIT',
};

const toEp1001HfsPaymentMethod = paymentMethod => ep1001HfsPaymentMethods[paymentMethod] || paymentMethod;

export default {
  createEPCT: async (iframeCall, options) => {
    const { data: resp } = await httpRequest(`${baseUrl}/pct`, {
      method: 'POST',
      data: iframeCall,
      ...options,
    })();
    return sanitize(resp);
  },
  editEPCT: async (authToken, submissionId, procedure, options) => {
    const { data: resp } = await httpRequest(`${baseUrl}/pct/${submissionId}`, {
      method: 'PUT',
      data: { authToken, procedure },
      ...options,
    })();
    return sanitize(resp);
  },
  lockById: async (draftId, options) => {
    const { data: resp } = await httpRequest(`${tempUrl}/${draftId}/lock`, {
      method: 'PATCH',
      ...options,
    })();
    return sanitize(resp);
  },
  deleteSubmissions: async (submissionIds, options) => {
    const { data: resp } = await httpRequest(`${tempUrl}`, {
      method: 'DELETE',
      data: submissionIds,
      ...options,
    })();
    return resp;
  },
  sendToSignSubmissions: async (draftIds, options) => {
    const { data: resp } = await httpRequest(`${tempUrl}/signatures`, {
      method: 'PATCH',
      data: draftIds,
      ...options,
    })();
    return resp;
  },
  validateSubmission: async (draftId, isLocked, options) => {
    const { data: resp } = await httpRequest(`${tempUrl}/${draftId}/validate/signatures`, {
      method: 'GET',
      params: {
        fromDraft: isLocked,
      },
      ...options,
    })();
    return resp;
  },
  getAuthToken: async (procedure, options) => {
    const tokenUrl = procedure === 'PCTRO101' ? epctROInitUrl : epctSFDInitUrl;
    await httpRequest(tokenUrl, {
      method: 'GET',
      withDefaultPath: false,
      ...options,
    })();
    return getCookie('authToken');
  },
  createEP: async (iframeCall, options) => {
    const jsonDataModel = {
      formType: 'EP 1001',
      userReference: { value: iframeCall.fileReference },
      proceedingRole: { value: iframeCall.proceedingRole },
      paymentMode: { value: toEp1001HfsPaymentMethod(iframeCall.paymentMethods) },
      isProceduralLanguage: { value: iframeCall.requestLanguage },
    };
    const { data: resp } = await httpRequest(`${baseUrl}/ep/submissions`, {
      method: 'POST',
      data: {
        proceduralLanguageCode: iframeCall.requestLanguage,
        submissionType: 'SUBMISSION',
        submissionReference: iframeCall.fileReference,
        jsonDataModel: JSON.stringify(jsonDataModel),
      },
      ...options,
    })();
    return sanitize(resp);
  },
  saveEP: async (token, submissionId, options) => {
    const { data: resp } = await httpRequest(`${baseUrl}/ep/submissions/save`, {
      method: 'POST',
      params: {
        submissionId,
      },
      data: {
        token,
      },
      ...options,
    })();
    return sanitize(resp);
  },
  iframeUrl: async (options) => {
    const { data: resp } = await httpRequest(`${baseUrl}/ep/iframe-url`, {
      method: 'GET',
      ...options,
    })();
    return sanitize(resp);
  },
  editEP: async (authToken, submissionId, options) => {
    const { data: resp } = await httpRequest(`${baseUrl}/ep/submissions/edit/${submissionId}`, {
      method: 'POST',
      data: { authToken },
      ...options,
    })();
    return sanitize(resp);
  },
};
